/**
 * An array of theme colors.
 * - "primary": (default) blue with white text
 * - "secondary": lighter blue with white text
 * - "tertiary": darker gray
 * - "gray": gray with black text
 * - "danger": red with white text
 * - "success": green with white text
 * - "warning": yellow with white text
 */
export const themeColors = ["primary", "secondary", "tertiary", "gray", "success", "warning", "danger"] as const;

/**
 * An array of theme types.
 * - "filled": (default) colored with white/black text
 * - "outline": border with text the same color as the border
 * - "clear": transparent with colored text
 * - "card": vertical flex box with 265px height
 */
export const themeTypes = ["filled", "outline", "clear", "card"] as const;

/**
 * An array of theme sizes.
 * - "fit": no padding, inherit
 * - "thin": half the padding
 * - "medium": (default) medium padding
 * - "huge": large padding
 */
export const themeSizes = ["fit", "thin", "medium", "huge"] as const;

/**
 * Type representing a theme color.
 */
export type ThemeColor = (typeof themeColors)[number];

/**
 * Type representing a theme type.
 */
export type ThemeType = (typeof themeTypes)[number];

/**
 * Type representing a theme size.
 */
export type ThemeSize = (typeof themeSizes)[number];

/**
 * Type representing a theme, which can be a combination of theme color, type, and size.
 * - ThemeColor
 * - ThemeType
 * - ThemeSize
 * - `${ThemeColor}-${ThemeType}`
 * - `${ThemeColor}-${ThemeSize}`
 * - `${ThemeType}-${ThemeSize}`
 * - `${ThemeColor}-${ThemeType}-${ThemeSize}`
 */
export type Theme =
  | ThemeColor
  | ThemeType
  | ThemeSize
  | `${ThemeColor}-${ThemeType}`
  | `${ThemeColor}-${ThemeSize}`
  | `${ThemeType}-${ThemeSize}`
  | `${ThemeColor}-${ThemeType}-${ThemeSize}`;
